import React, { useEffect, useRef, useState } from 'react'
import {
    advanceWalletPasskeyStep,
    loadWalletPasskeyStatus,
    pickRestorePasskey,
    prepareWalletPasskeyAction,
    type WalletPasskeyStep,
} from 'lib/walletPasskeyRecovery'
import {
    passkeyBrowserUnavailableReason,
    walletPasskeyErrorMessage,
} from 'lib/walletPasskeyWebAuthn'
import { readWalletBackupFile, restoreWalletFromBackupText } from 'lib/walletFileBackup'
import { resetUnbackedLocalWallet } from 'lib/localWallet'
import { CLIENT_SIGNING_READY_EVENT } from 'lib/clientSigningFeature'
import styles from './walletSetup.module.scss'

// Signed in on a device that doesn't hold this account's browser wallet (a new phone,
// cleared site data): unlock it with the passkey backup, or the wallet file.
export default function WalletRestore({
    userId,
    backedUp,
    onClose,
    onStartOver,
    onRestored,
}: {
    userId: string
    backedUp: boolean
    onClose: () => void
    onStartOver: () => void
    onRestored: () => void
}) {
    const [step, setStep] = useState<WalletPasskeyStep | null>(null)
    const [checking, setChecking] = useState(true)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const stepRef = useRef<WalletPasskeyStep | null>(null)
    // Passkeys that failed to unlock here; the next try offers another verified one first.
    const triedPasskeys = useRef(new Set<string>())
    const fileInput = useRef<HTMLInputElement>(null)
    const passkeysAvailable = !passkeyBrowserUnavailableReason()

    const prepare = async () => {
        stepRef.current = null
        setStep(null)
        if (!passkeysAvailable) {
            setChecking(false)
            return
        }
        try {
            const status = await loadWalletPasskeyStatus()
            let passkey = pickRestorePasskey(status, undefined, triedPasskeys.current)
            // Every passkey tried (a cancelled prompt counts too): go round again, so a
            // single passkey can always be retried.
            if (!passkey && triedPasskeys.current.size) {
                triedPasskeys.current.clear()
                passkey = pickRestorePasskey(status)
            }
            if (passkey) {
                const prepared = await prepareWalletPasskeyAction(
                    passkey,
                    status.identity,
                    'recover',
                )
                stepRef.current = prepared
                setStep(prepared)
            }
        } catch (caught) {
            setError(walletPasskeyErrorMessage(caught))
        } finally {
            setChecking(false)
        }
    }

    useEffect(() => {
        void prepare()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId])

    const startOver = async () => {
        if (busy) return
        setBusy(true)
        setError('')
        try {
            await resetUnbackedLocalWallet()
            onStartOver()
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : String(caught))
        } finally {
            setBusy(false)
        }
    }

    const finish = () => {
        // Start listening for signing requests with the restored wallet, no reload needed.
        window.dispatchEvent(new Event(CLIENT_SIGNING_READY_EVENT))
        onRestored()
    }

    const unlockWithPasskey = () => {
        const prepared = stepRef.current
        if (!prepared || busy) return
        setBusy(true)
        setError('')
        // No await before this call: it opens the passkey sheet inside the tap.
        advanceWalletPasskeyStep(prepared)
            .then(result => {
                if (result.recovered) finish()
            })
            .catch(caught => {
                setError(walletPasskeyErrorMessage(caught))
                if (prepared.kind === 'recover') triedPasskeys.current.add(prepared.passkey.id)
                void prepare()
            })
            .finally(() => setBusy(false))
    }

    const restoreFromFile = async (file: File | undefined) => {
        if (!file || busy) return
        setBusy(true)
        setError('')
        try {
            await restoreWalletFromBackupText(await readWalletBackupFile(file))
            finish()
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : String(caught))
        } finally {
            setBusy(false)
        }
    }

    const passkeyFirst = !!step
    return (
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-label="Unlock your wallet"
        >
            <div className={styles.screen}>
                <div className={styles.topBar}>
                    <span />
                    {/* Always a way past: without the file, and with a gift already paid,
                        neither restoring nor starting over is possible on this device. */}
                    <button type="button" className={styles.later} onClick={onClose}>
                        Not now
                    </button>
                </div>
                <div className={styles.body}>
                    <h1 className={styles.title}>
                        {checking
                            ? 'Unlock your wallet'
                            : passkeyFirst
                            ? 'Unlock your wallet on this phone'
                            : 'Your wallet is still locked'}
                    </h1>
                    <p className={styles.text}>
                        {checking
                            ? 'Looking for your wallet backup…'
                            : passkeyFirst
                            ? 'You’re signed in. Approve your passkey once more to bring your wallet here.'
                            : backedUp
                            ? 'Your wallet isn’t on this device, and no passkey here can open its backup. Restore it with your wallet file.'
                            : 'Your wallet was never backed up and isn’t on this device. Restore it from a wallet file if you saved one, or start a new wallet.'}
                    </p>
                </div>
                <div className={styles.actions}>
                    {error && (
                        <p className={styles.error} role="alert">
                            {error}
                        </p>
                    )}
                    <input
                        ref={fileInput}
                        type="file"
                        accept="application/json,.json"
                        className={styles.hiddenInput}
                        hidden
                        onChange={event => {
                            void restoreFromFile(event.target.files?.[0])
                            event.target.value = ''
                        }}
                    />
                    {passkeyFirst ? (
                        <>
                            <button
                                type="button"
                                className={styles.primary}
                                onClick={unlockWithPasskey}
                                disabled={busy}
                                data-testid="wallet-restore-passkey"
                            >
                                {busy ? 'Waiting for your passkey…' : 'Unlock with passkey'}
                            </button>
                            <button
                                type="button"
                                className={styles.quiet}
                                onClick={() => fileInput.current?.click()}
                                disabled={busy}
                            >
                                Use wallet file instead
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={styles.primary}
                                onClick={() => fileInput.current?.click()}
                                disabled={busy || checking}
                                data-testid="wallet-restore-file"
                            >
                                {busy ? 'Restoring…' : 'Restore from wallet file'}
                            </button>
                            {backedUp ? (
                                <button type="button" className={styles.quiet} onClick={onClose}>
                                    Not now
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.quiet}
                                    onClick={() => void startOver()}
                                    disabled={busy || checking}
                                    data-testid="wallet-restore-start-over"
                                >
                                    Start a new wallet
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
